import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

// --- เพิ่ม FiShield สำหรับไอคอนความเป็นส่วนตัว ---
const { FiLoader, FiUpload, FiInfo, FiShield } = FiIcons;

const getMediaDuration = (file) => {
  return new Promise((resolve) => {
    try {
      if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
        resolve(0);
        return;
      }
      const url = URL.createObjectURL(file);
      const media = new Audio(url);
      const cleanup = () => {
        setTimeout(() => URL.revokeObjectURL(url), 100);
      };
      media.onloadedmetadata = () => {
        resolve(media.duration);
        cleanup();
      };
      media.onerror = (err) => {
        console.warn("Could not get media duration:", err);
        resolve(0);
        cleanup();
      };
    } catch (error) {
      console.warn("Error in getMediaDuration:", error);
      resolve(0);
    }
  });
};

const FileUpload = ({ onFileUpload, isLoading }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const processFile = useCallback(
    async (file, skipTranscription = false) => {
      if (file && !isLoading) {
        const maxSize = 200 * 1024 * 1024; // 200MB
        if (file.size > maxSize && !skipTranscription) {
          alert(
            `File is too large (${(file.size / 1024 / 1024).toFixed(
              2,
            )} MB). Please use the 'Upload Large File' button.`,
          );
          return;
        }

        let duration = 0;
        if (!skipTranscription) {
          try {
            duration = await getMediaDuration(file);
            const maxDuration = 150 * 60; // 150 minutes (2.5 hours)
            if (duration > maxDuration) {
              const userConfirmed = window.confirm(
                "การประชุม เกิน 2.5 ชม คุยสามารถทำรายการต่อไปได้ แต่ว่า คำพูดที่ถอดอาจไม่ครบถ้วน (แต่คุณยังใช้งานได้ปกติผ่าน attach file)\n\nคุณต้องการดำเนินการต่อหรือไม่?",
              );
              if (!userConfirmed) {
                return; // Stop the process if the user cancels
              }
            }
          } catch (error) {
            console.error("Error checking media duration:", error);
            // Optionally, inform the user that the duration check failed and proceed anyway.
            const userConfirmed = window.confirm(
              "ไม่สามารถตรวจสอบความยาวของไฟล์ได้ คุณยังต้องการอัปโหลดต่อหรือไม่?",
            );
            if (!userConfirmed) {
              return;
            }
          }
        }

        onFileUpload(file, skipTranscription, duration);
      }
    },
    [onFileUpload, isLoading],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        processFile(e.dataTransfer.files[0]);
      }
    },
    [processFile]
  );

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files && e.target.files[0]) {
        processFile(e.target.files[0]);
      }
      e.target.value = "";
    },
    [processFile]
  );

  return (
    <div className="w-full max-w-lg mx-auto">
      <label
        htmlFor="file-upload-input"
        className={`
          relative block p-4 border rounded-2xl cursor-pointer
          transition-all duration-300 ease-in-out
          ${
            dragActive
              ? "border-green-400 bg-green-50 scale-105 shadow-lg"
              : "border-slate-200 bg-white hover:border-green-300 hover:shadow-md"
          }
          ${isLoading ? "pointer-events-none" : ""}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          id="file-upload-input"
          type="file"
          accept="audio/*,video/*,.mp3,.mp4,.m4a,.wav,.webm,.mov"
          onChange={handleFileInput}
          className="sr-only"
          disabled={isLoading}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={isLoading ? "loading" : "idle"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center text-center"
          >
            {isLoading ? (
              // --- LOADING STATE (ลด py) ---
              <div className="flex flex-col items-center justify-center text-slate-500 py-4">
                <SafeIcon
                  icon={FiLoader}
                  className="w-10 h-10 animate-spin text-green-600 mb-4"
                />
                <p className="font-semibold text-lg text-slate-700">
                  กำลังประมวลผล...
                </p>
                <p className="text-sm">ไฟล์ของคุณกำลังถูกเตรียมพร้อม</p>
              </div>
            ) : (
              // --- IDLE STATE (ลด py) ---
              <div className="flex flex-col items-center justify-center text-slate-600 py-1">
                <div
                  className={`
                    p-4 rounded-full transition-colors duration-300
                    ${dragActive ? "bg-green-100" : "bg-slate-100"}
                  `}
                >
                  <SafeIcon
                    icon={FiUpload}
                    className={`w-10 h-10 transition-colors duration-300 ${
                      dragActive ? "text-green-600" : "text-slate-500"
                    }`}
                  />
                </div>
                <p className="font-semibold text-lg mt-3">ลากไฟล์มาวางที่นี่</p>
                <p className="text-slate-500 text-sm mt-1">
                  หรือ{" "}
                  <span className="font-semibold text-green-600 hover:text-green-700">
                    คลิกเพื่อเลือกไฟล์
                  </span>
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {isLoading && (
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <div className="absolute top-0 left-0 w-full h-full -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite]"></div>
          </div>
        )}
      </label>

      {/* --- INFORMATIONAL TEXT (ลด mt) --- */}
      <div className="text-left mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200/80 flex items-start gap-3">
        <SafeIcon
          icon={FiInfo}
          className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0"
        />
        <div>
          <p className="text-sm text-slate-700 font-semibold">
            เพื่อประสิทธิภาพสูงสุด:
          </p>
          <ul className="list-disc list-inside text-xs text-slate-500 mt-1 space-y-1">
            <li>แนะนำไฟล์ความยาวไม่เกิน 1 ชั่วโมง หรือขนาด 200MB (สำหรับการถอดความ)</li>
            <li>ไฟล์ใหญ่ (ข้ามการถอดความ) ขนาดไม่เกิน 300MB</li>
            <li>ไฟล์ 1 ชั่วโมง อาจใช้เวลาถอดความประมาณ 6-7 นาที</li>
          </ul>
        </div>
      </div>

      {/* --- START: Privacy Remark (เพิ่มใหม่) --- */}
      <div className="text-center mt-3 text-xs text-slate-500 flex items-center justify-center gap-2">
        <SafeIcon icon={FiShield} className="w-4 h-4 text-green-600" />
        <span>
          <span className="font-semibold">เพื่อความเป็นส่วนตัวของคุณ:</span>{" "}
          เราไม่เก็บไฟล์ต้นฉบับและข้อมูลการถอดความไว้บนระบบ
        </span>
      </div>
      {/* --- END: Privacy Remark --- */}

      {/* --- START: Large File Upload Button --- */}
      <div className="text-center mt-4">
        <p className="text-md text-slate-600 mb-2">
          ไฟล์ใหญ่เกิน 200MB (สูงสุด 300MB)?
        </p>
        <button
          onClick={() => {
            // Trigger hidden file input
            document.getElementById('large-file-upload-input').click();
          }}
          disabled={isLoading}
          className="
            px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold
            text-slate-700 bg-white hover:bg-slate-50
            disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed
            transition-all duration-200
          "
        >
          อัปโหลดไฟล์ใหญ่ (ข้ามการถอดความ)
        </button>
        <input
          id="large-file-upload-input"
          type="file"
          accept="audio/*,video/*,.mp3,.mp4,.m4a,.wav,.webm,.mov"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              const file = e.target.files[0];
              const largeFileMaxSize = 300 * 1024 * 1024; // 300MB

              if (file.size > largeFileMaxSize) {
                alert(
                  `ไฟล์มีขนาดใหญ่เกินไป (${(
                    file.size /
                    1024 /
                    1024
                  ).toFixed(2)} MB). ขนาดสูงสุดที่อนุญาตคือ 300 MB.`,
                );
                e.target.value = ""; // Reset the input
                return;
              }
              processFile(file, true, 0);
            }
            e.target.value = "";
          }}
          className="sr-only"
          disabled={isLoading}
        />
      </div>
      {/* --- END: Large File Upload Button --- */}
    </div>
  );
};

export default FileUpload;
